type UUIDResponse = {
  UUID?: string;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ANONYMOUS_ID_STORAGE_KEY = 'anonymousId';

export function readPersistedAnonymousId(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const stored = window.localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
    return stored && UUID_PATTERN.test(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export function persistAnonymousId(uuid: string): void {
  if (typeof window === 'undefined' || !UUID_PATTERN.test(uuid)) {
    return;
  }
  try {
    window.localStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, uuid);
  } catch {
    /* localStorage unavailable (private mode / quota) — non-fatal */
  }
}

const ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 400; // 400 days

export function persistAnonymousIdCookie(uuid: string): void {
  if (typeof document === 'undefined' || !UUID_PATTERN.test(uuid)) {
    return;
  }
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${ANONYMOUS_ID_STORAGE_KEY}=${uuid}; Path=/; Max-Age=${ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS}` +
      `; SameSite=Lax${secure}`;
  } catch {
    /* document.cookie unavailable — non-fatal */
  }
}

export function persistResolvedAnonymousId(uuid: string): void {
  persistAnonymousId(uuid);
  persistAnonymousIdCookie(uuid);
}

export async function getUUID(
  cookieHandlerUrl: string,
  { seed, signal }: { seed?: string; signal?: AbortSignal } = {},
) {
  const seedQuery = seed && UUID_PATTERN.test(seed) ? `?seed=${encodeURIComponent(seed)}` : '';
  const response = await fetch(`${cookieHandlerUrl}/uuid/get-uuid${seedQuery}`, {
    method: 'GET',
    credentials: 'include',
    signal,
  });

  if (!response.ok) {
    return reportUUIDError({
      cookieHandlerUrl,
      error: await response.text(),
    });
  }
  const data = (await response.json()) as UUIDResponse;
  if (!data?.UUID) {
    return reportUUIDError({
      cookieHandlerUrl,
      error: { data, uuid: 'no uuid' },
    });
  }
  return data?.UUID ?? null;
}

/**
 * Report UUID-related errors to Cookie Handler.
 */
export async function reportUUIDError({
  cookieHandlerUrl,
  error,
  project = 'Website',
  url,
}: {
  cookieHandlerUrl: string;
  error: unknown;
  project?: string;
  url?: string;
}) {
  try {
    await fetch(`${cookieHandlerUrl}/uuid/error`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        error: {
          error,
          project,
          url,
        },
      }),
    });
  } catch (e) {
    console.error(e);
  }
}

/**
 * Apply UUID side-effects on the client without fetching.
 * Uses intervals to wait for global libraries (Segment, dataLayer).
 */
export function applyUUIDToWindow({
  uuid,
  gtmId,
  oneTrustDomainScript,
}: {
  uuid: string;
  gtmId?: string;
  oneTrustDomainScript?: string;
}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).dataLayer = (window as any).dataLayer || [];

  const loadGTMScript = () => {
    if (!gtmId) return;

    const existing = document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`);
    if (existing) return;

    // Load Google Tag Manager dynamically
    (function (w, d, s, l, i) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (w as any)[l] = (w as any)[l] || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (w as any)[l].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });
      const f = d.getElementsByTagName(s)[0] as HTMLElement | undefined;
      const j = d.createElement(s) as HTMLScriptElement;
      const dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f?.parentNode?.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', gtmId);
  };

  const loadOneTrust = () => {
    if (!oneTrustDomainScript) return;

    const existing = document.querySelector(
      `script[src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"][data-domain-script="${oneTrustDomainScript}"]`,
    );
    if (existing) return;

    const oneTrustScript = document.createElement('script');
    oneTrustScript.type = 'text/javascript';
    oneTrustScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    oneTrustScript.dataset.domainScript = oneTrustDomainScript;
    oneTrustScript.charset = 'UTF-8';
    document.head.appendChild(oneTrustScript);

    const oneTrustScriptCall = document.createElement('script');
    oneTrustScriptCall.type = 'text/javascript';
    oneTrustScriptCall.innerHTML = 'function OptanonWrapper() { }';
    document.head.appendChild(oneTrustScriptCall);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).anonymousId = uuid;

  // Ensure UUID is at the beginning of the dataLayer
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dl = (window as any).dataLayer as any[];
  if (!dl.find((i) => i?.uuid)) {
    dl.push({ uuid, event: 'uuid' });
  }

  const timerSegment = setInterval(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const analytics = (window as any).analytics as any;
    if (analytics && uuid) {
      analytics.setAnonymousId(uuid);
      analytics.page();
      clearInterval(timerSegment);
      loadGTMScript();
    }
  }, 100);

  const timerGTM = setInterval(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataLayer = (window as any).dataLayer as any[] | undefined;
    if (!dataLayer || !uuid) return;

    if (!dataLayer.find((i) => i?.uuid)) {
      dataLayer.push({ uuid, event: 'uuid' });
      clearInterval(timerGTM);
    } else {
      clearInterval(timerGTM);
    }
  }, 100);

  // Continue loading other scripts after UUID is set
  loadOneTrust();

  return () => {
    clearInterval(timerSegment);
    clearInterval(timerGTM);
  };
}
